import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api, { fmtErr } from "../../lib/api";
import { toast } from "sonner";
import { MonitorPlay } from "lucide-react";

const RTC_CONFIG = { iceServers: [{ urls: "stun:stun.l.google.com:19302" }] };

export default function ShareScreen() {
  const { sessionId } = useParams();
  const [state, setState] = useState("idle");
  const pcRef = useRef(null);
  const pollRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => () => { clearInterval(pollRef.current); pcRef.current?.close(); streamRef.current?.getTracks().forEach((t) => t.stop()); }, []);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
      streamRef.current = stream;
      const pc = new RTCPeerConnection(RTC_CONFIG);
      pcRef.current = pc;
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      stream.getVideoTracks()[0].onended = () => stop();
      pc.onicecandidate = (e) => e.candidate && api.post(`/helpdesk/share/${sessionId}/candidate`, { side: "sharer", candidate: e.candidate.toJSON() });
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await api.post(`/helpdesk/share/${sessionId}/offer`, { sdp: { type: offer.type, sdp: offer.sdp } });
      setState("waiting");
      let connected = false, seen = 0;
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/helpdesk/share/${sessionId}/state`);
          if (data.answer && !connected) { connected = true; await pc.setRemoteDescription(data.answer); setState("sharing"); }
          for (const c of (data.viewer_candidates || []).slice(seen)) { seen++; await pc.addIceCandidate(c).catch(() => {}); }
          if (data.status === "ended") stop();
        } catch { /* keep polling */ }
      }, 1200);
    } catch (e) { toast.error(fmtErr(e) || "Screen share cancelled"); }
  };

  const stop = async () => {
    clearInterval(pollRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    pcRef.current?.close();
    await api.post(`/helpdesk/share/${sessionId}/end`).catch(() => {});
    setState("ended");
  };

  return (
    <div className="max-w-lg mx-auto mt-16 bg-white border border-neutral-200 rounded-2xl p-8 text-center" data-testid="share-screen-page">
      <MonitorPlay size={36} className="mx-auto text-sky-600" />
      <h1 className="text-xl font-bold font-heading mt-4">Share your screen with support</h1>
      <p className="text-sm text-neutral-500 mt-2">
        {state === "idle" && "Click below and pick the screen or window to share. The support agent can only watch — nothing is installed or controlled."}
        {state === "waiting" && "Screen selected — waiting for the agent to connect..."}
        {state === "sharing" && "You are sharing your screen live. Click stop when finished."}
        {state === "ended" && "Session ended. You can close this page."}
      </p>
      {state === "idle" && (
        <button data-testid="start-sharing-button" onClick={start}
          className="mt-6 px-7 py-2.5 rounded-lg text-white bg-sky-600 hover:bg-sky-700 text-sm font-medium">Share my screen</button>
      )}
      {(state === "waiting" || state === "sharing") && (
        <button data-testid="stop-sharing-button" onClick={stop}
          className="mt-6 px-7 py-2.5 rounded-lg text-white bg-red-600 hover:bg-red-700 text-sm font-medium">Stop sharing</button>
      )}
    </div>
  );
}
